import * as React from 'react'
import { useMemo } from 'react'
import {
  type IOption,
  type IProviderProps,
  isIOptionArray,
  optionDependencyToNumber,
} from '@user/components/Filters'
import { useAppSelector } from '@user/store/hooks'
import { selectAllUsers, useGetUserListOnMount } from '@user/store/users'
import { useCheckAdminPermissions } from '@user/store/authorization/permissions/useCheckPermissions'
import { SettingsAdminPermOps } from '@user/store/authorization'

export function OwnerProvider(props: IProviderProps) {
  const { render, dependsOn } = props

  const status = useGetUserListOnMount(true)
  const users = useAppSelector(selectAllUsers)

  const loading = status === 'pending'

  const [showAdminUsers] = useCheckAdminPermissions(SettingsAdminPermOps)

  const selectedSalesTeamIds = useMemo(
    () =>
      isIOptionArray(dependsOn) ? dependsOn.map(optionDependencyToNumber) : [],
    [dependsOn],
  )

  const options = useMemo(() => {
    if (users.length === 0) return []

    const opts = users
      .filter(
        (user) =>
          user.isActive && (!showAdminUsers ? user.roleName !== 'Admin' : true),
      )
      .filter(({ salesTeams }) => {
        if (selectedSalesTeamIds.length > 0 && salesTeams.length > 0)
          return salesTeams.some(({ id }) => selectedSalesTeamIds.includes(id))
        return true
      })
      .map<IOption>((u) => ({
        value: u.id,
        label: u.fullName ?? '',
      }))

    const unassigned: IOption = {
      value: null,
      label: 'Unassigned',
    }

    const sortedOpts = [
      unassigned,
      ...opts.sort((a, b) => a.label.localeCompare(b.label)),
    ]

    return sortedOpts
  }, [selectedSalesTeamIds, showAdminUsers, users])

  return <>{render(options, loading)}</>
}
